import { formatIsoDayRange } from '@revelio/core/global-ai-assistant/iso-day';
// The alias map and this function live in libs/core so a spec can compare them
// against the titles dash-meta stamps — see that module's doc.
import { narrationMetricLabel } from '@revelio/core/global-ai-assistant/metric-labels';
import type { ChartValueForm } from '@revelio/core/global-ai-assistant/share-display';
import type {
  CompanyDiscoveryAiOutput,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';
import { FormatType, getFormatter } from '@revelio/replots';

import { LocationType } from '../CustomDash/components/Chart/Map/types';
import { getFormat } from '../CustomDash/components/Chart/utils/getFormat';
import { isHeadcountShareDisplayedOnLine } from '../CustomDash/components/Chart/utils/share-display';
import { DataType } from '../CustomDash/components/DataProvider/types';
import type {
  MetricValues,
  PostingMetricValues,
} from '../CustomDash/components/PageConfigProvider/types/metric';
import type { GlobalAssistantReportParamSuggestion } from './global-ai-assistant-api';

// Re-exported: this module is the narration hub every caller already imports
// from, and moving the definition should not move every call site.
export { narrationMetricLabel };

/**
 * Belt-and-braces backstop for the metric-label change: the digests the model
 * reads no longer contain a raw metric key, so there is nothing to echo — but a
 * model can still invent one from a chart title. Rides every narration that
 * summarizes chart digests (the page-build results in
 * client-tools/tool-result-copy.ts and the seeded landing narration in
 * seeded-build-narration.ts). Lives here rather than with the rest of the
 * result copy because both of those import their digest rendering from this
 * module already. Reported on stage 2026-08-11: "shows attrition_rate at 11.4%
 * and hiring_rate at 9.7%".
 */
export const NATURAL_METRIC_NAME_INSTRUCTION =
  'Name every metric in plain English ("attrition rate", "employee sentiment") — NEVER an internal identifier such as attrition_rate or sentiment_top_bottom, and never any snake_case token.';

export type DigestCategory = {
  label: string;
  value: number;
  displayValue?: string;
  /**
   * Entity id of the series/category row when the provider payload carries one
   * (role/geo EntID, company rcid) — lets breakdown answers rebuild filters
   * deterministically without a lossy label lookup.
   */
  id?: string | number;
  /** First→latest change of this series over the shown window, in % (multi-series timeseries only). */
  deltaPct?: number;
  /**
   * Segment size (headcount) when `value` is a RATE — the top-segment
   * breakdowns plot growth per segment, so this is how the answer states how
   * BIG each segment is ("Sales, 890 people, +14%") and how a plain
   * "most common / biggest" ask gets its ranking figure.
   */
  segmentHeadcount?: number;
  /** Display-formatted `segmentHeadcount` (thousands separators). */
  segmentHeadcountDisplay?: string;
  /**
   * The category's NATIVE share of the breakdown's classified total, as a
   * display percentage (e.g. "51%"), when the provider payload carries one.
   * This is the backend's own share field — its denominator is the sum of the
   * CLASSIFIED breakdown rows, never the entity-wide headcount — so it is the
   * only share a "what share is X" answer may cite. Absent when the data path
   * has no honest share (e.g. a segment bar summed across several entities).
   */
  shareDisplay?: string;
  /**
   * Set when `value` is a distribution's MEDIAN: the 25th–75th percentile
   * range, display-formatted. The answer to "show me the salary distribution"
   * is its middle and its spread, which the histogram's bins never state.
   */
  middleHalfDisplay?: string;
};

export type ChartDataDigest = {
  kind: 'chart_data';
  title?: string;
  /**
   * Level vs share, as the single-chart model classified the ask, or
   * 'unclassified' when that chart arrived without the classification. Absent
   * on page-build digests: dashboards are never asked to classify.
   */
  valueForm?: ChartValueForm | 'unclassified';
  /**
   * The INTERNAL metric key. Kept because it drives logic — value formatting
   * here, and the eval recorder the per-tool modules call — but it must never
   * reach model-facing text: narrate through `metricLabel` instead.
   */
  metric: string;
  /**
   * Display name of `metric` (the metric catalog's label). Every summary the
   * model reads names the metric through this, because a raw key in that text
   * gets echoed straight at the user: a London attrition dashboard was
   * narrated as "shows attrition_rate at 11.4% and hiring_rate at 9.7% …
   * while sentiment_top_bottom is strongest on Commute" (stage, 2026-08-11).
   *
   * Optional only because a few paths build a digest as a literal (the
   * headless error digests, the turn runner's synthetic empties); readers
   * resolve `metricLabel ?? narrationMetricLabel(metric)`, which is the same
   * function that fills it here, so an unset field is never a raw-key leak.
   */
  metricLabel?: string;
  /**
   * Set when a posting-count chart was fetched with `mode: HIRES`: the figures
   * estimate hires, not postings. Read from the request actually sent.
   */
  postingsWeighting?: 'expected_hires';
  chartType: string;
  rowCount: number;
  latestValue?: number;
  latestValueDisplay?: string;
  /** Earliest value of the primary series (pairs with `firstDate`). */
  firstValue?: number;
  firstValueDisplay?: string;
  /**
   * Highest value of the primary series and the date it occurred. Lets narration
   * anchor on the VISIBLE peak ("down from a 50% peak in 2019") rather than the
   * series' first point, which for rate metrics (hiring/attrition/growth) is
   * often an unrepresentative early spike that `deltaPct` over-weights.
   */
  peakValue?: number;
  peakValueDisplay?: string;
  peakDate?: string;
  /** Whole-series change (first→last). Can mislead when the first point is an early spike — pair it with the actual endpoint values when narrating. */
  deltaPct?: number;
  changePpDisplay?: string;
  /** First/last date of the primary series, when time-based. */
  firstDate?: string;
  lastDate?: string;
  topCategories?: DigestCategory[];
  /**
   * Ranked WORST end of a categorical breakdown (bottom-5, worst first) —
   * only emitted when the breakdown has more rows than `topCategories`
   * covers. For `sentiment_top_bottom` this is the attrition-factor side:
   * the lowest-rated aspects a "why are people leaving" answer cites as its
   * grounded driver signals.
   */
  bottomCategories?: DigestCategory[];
  empty: boolean;
  /**
   * Labels of the demographic/entity filters the chart was ACTUALLY rendered
   * with (e.g. `["company: Google LLC", "ethnicity", "gender"]`). An EXPLICIT,
   * structured record that those filters resolved + applied, so the grounded
   * narration cannot infer "unfiltered" from `rowCount: 1` + a bare metric and
   * invent "the filter did not resolve" — the title alone (a description)
   * wasn't trusted against a single-row result.
   */
  filtersApplied?: string[];
  /**
   * Resolved filters the chart could NOT apply (e.g. `["geography (excluded)"]`),
   * so its figure counts a wider population than the one asked about.
   */
  filtersNotApplied?: string[];
  /**
   * The RESOLVED display label(s) of the applied role filter (from the chart
   * scope's labels-patched `activeFilterMap`, i.e. the same names the filter
   * chips render). The AI response carries only taxonomy-relative numeric role
   * ids, so `filtersApplied` can state a bare `role` at best — this field is
   * what lets the narration name the role the backend ACTUALLY resolved
   * ("Aircraft Mechanics") instead of parroting the user's own term
   * ("nurses") when resolution landed somewhere else.
   */
  resolvedRoleLabel?: string;
  /**
   * Skill twin of `resolvedRoleLabel`: the RESOLVED display label(s) of the
   * applied skill filter. Skill ids are equally taxonomy-relative, so without
   * this the narration parrots the user's phrasing ("finance skill") even
   * when resolution landed on specific taxonomy skills.
   */
  resolvedSkillLabel?: string;
  /**
   * Activity twin of `resolvedSkillLabel`: activity ids are equally
   * taxonomy-relative, so this is the only place the narration learns which
   * activities the filter actually resolved to.
   */
  resolvedActivityLabel?: string;
  /**
   * Set when the chart settled in an ERROR phase (data layer failed to load).
   * Distinguishes a render/data FAILURE from a genuinely-empty result so the
   * model narrates "couldn't load that chart" instead of "no data".
   */
  error?: true;
  /**
   * Set when the chart NEVER SETTLED before the wait gave up (settle timeout,
   * headless-digest timeout, or abort) — the third case the synthetic
   * `{rowCount: 0, empty: true}` shape used to collapse into "empty". A chart
   * that never finished loading tells us NOTHING about its data, so the
   * summary must never read as "the chart has no data" (`empty` stays true
   * only to satisfy the required field; `chartSummary` branches before it).
   * Serialized as an ERROR-prefixed summary on purpose: a chart that never
   * appeared is not an analytical outcome, so the ERROR prefix is what keeps
   * `isClientToolFailure` true and the report ride-along DISARMED for it —
   * same contract as `error`. A digest that genuinely settled empty never
   * carries this flag and keeps its current wording.
   *
   * Pair with `displayed` when the card IS on screen — see its docblock.
   */
  unsettled?: true;
  /**
   * Only ever set alongside `unsettled`, and only on the VISIBLE `render_chart`
   * path, where the card is appended to the transcript BEFORE the settle wait
   * starts. The chart is on screen and still loading — the timeout does not
   * unmount it, so it paints its data moments later. Telling the user "the data
   * could not be loaded" about a card they can see is wrong, and treating it as
   * a tool FAILURE also disarms the report ride-along that a displayed chart
   * has always armed. So this keeps the summary non-ERROR while still barring
   * the model from stating any figure.
   */
  displayed?: true;
  /** How many categories the breakdown has, of which the lists show the ends. */
  categoryCount?: number;
  /**
   * A trend chart drawn as ONE line. Narration kept telling users such a
   * chart "breaks out" postings by role because the request asked for roles;
   * this states the shape from the data instead.
   */
  singleSeries?: true;
  /**
   * Categories carry their share of the classified total, but the chart draws
   * one bar of COUNTS per category, so the shares are not on screen.
   */
  sharesBesideCounts?: true;
  /**
   * Plain-language reason for `error`, when one is known. Without it the model
   * only learns "something failed" and narrates a transient-sounding "couldn't
   * resolve that, please try again" — wrong and un-actionable when the cause is
   * permanent, e.g. a company the user's plan does not include. Narration
   * should state THIS reason rather than guessing at the failure.
   */
  errorReason?: string;
};

export type FilterResolutionDigest = {
  kind: 'filter_resolution';
  applied: Array<{ requested: string; resolvedTo: string; filterId: string }>;
  unresolved: Array<{ requested: string; reason?: string }>;
  targetPage: string;
};

export type ReportPrefillDigest = {
  kind: 'report_prefill';
  reportName: string;
  resolved: Array<{ parameter: string; value: string }>;
  skipped: Array<{ parameter: string; searchTerm: string; reason: string }>;
};

type Row = Record<string, unknown>;

function isRow(value: unknown): value is Row {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function rowArray(value: unknown): Row[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRow);
}

/**
 * What the digest works from after unwrapping the provider payload:
 * `rows` carries the primary series (time series points or categorical
 * entries); `seriesLatest` carries one latest-value entry per series when
 * the payload holds several (multi-series timeseries / breakdowns).
 */
type ChartShape = {
  rows: Row[];
  seriesLatest?: Array<{
    label: string;
    value: number;
    id?: string | number;
    first?: number;
  }>;
  /**
   * The period `seriesLatest` was read at (see `anchorDateFor`). Carried out
   * so the headline figure can be reported for the SAME month the categories
   * describe — a digest that cites one date and lists categories from another
   * reads as contemporaneous and is not.
   */
  anchorDate?: string;
  /**
   * The rows are a map's places at one level. No place is a total of the
   * chart (a map's largest place is not the company's headcount), so the
   * digest carries no headline value for them.
   */
  places?: true;
  /**
   * How many series the chart DRAWS. `seriesLatest` drops a series with no
   * value at the anchor, so it undercounts a line that stops early.
   */
  seriesCount?: number;
};

function lastNumericValue(points: Row[]): number | undefined {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const value = points[i]?.value;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

/**
 * The date of a series' last finite point — the period the digest treats as
 * "latest" for that series.
 */
function lastNumericDate(points: Row[]): string | undefined {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const point = points[i];
    const value = point?.value;
    if (typeof value === 'number' && Number.isFinite(value)) {
      return typeof point?.date === 'string' ? point.date : undefined;
    }
  }
  return undefined;
}

/**
 * A series' finite value AT a specific date, or undefined when that series has
 * no data there.
 *
 * Breakdown categories must all be read from the SAME period. `lastNumericValue`
 * walks back to each series' own last finite point, so a segment whose data
 * stops early contributed its OLDER value to `topCategories` beside other
 * segments' current ones — undated, and the narration template presents them as
 * contemporaneous, so the model would state a stale share as current (Greptile
 * P1 on #6500). Categories are anchored to ONE date and a segment absent from
 * that period is omitted rather than back-filled. See `anchorDateFor` for how
 * that date is chosen.
 */
function valueAtDate(points: Row[], date: string): number | undefined {
  for (const point of points) {
    if (point?.date !== date) continue;
    const value = point?.value;
    return typeof value === 'number' && Number.isFinite(value)
      ? value
      : undefined;
  }
  return undefined;
}

/**
 * The period every series in a multi-series chart is read at: the LATEST month
 * any series still reports.
 *
 * NOT the primary series' last point, which is what this used to be.
 * `items.find(seriesType === 'primary') ?? first` is an arbitrary category on a
 * breakdown-over-time — one country among several — so when it happened to end
 * earlier than the rest, the digest reported an older month than the chart's
 * own right edge shows, and every series still reporting past it was dropped
 * (Beyza P2 on #6500).
 *
 * Taking the max is what keeps BOTH properties the digest needs. It is always
 * >= the primary's own last date, so it never reads older than the chart; and
 * a series that stops early is still omitted rather than back-filled, which is
 * the Greptile P1 fix `valueAtDate` exists for — Taiwan's June figure must not
 * sit beside July's as if contemporaneous.
 *
 * Note this is a max over LAST-FINITE dates, not over the raw month grid: a
 * trailing incomplete period whose values are all null cannot win, because no
 * series reports there.
 */
function anchorDateFor(seriesPoints: Row[][]): string | undefined {
  let latest: string | undefined;
  for (const points of seriesPoints) {
    const date = lastNumericDate(points);
    if (date !== undefined && (latest === undefined || date > latest)) {
      latest = date;
    }
  }
  return latest;
}

function firstNumericValue(points: Row[]): number | undefined {
  for (let i = 0; i < points.length; i += 1) {
    const value = points[i]?.value;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

function dimLabel(item: Row): string {
  const candidate = item.dim ?? item.longName ?? item.id;
  return typeof candidate === 'string' ? candidate : 'unknown';
}

/**
 * Unwrap the chart data the FE providers actually emit. CustomDash
 * `DataProviderSet`s are envelope objects (`{ type, data: [...] }`) whose
 * items are timeseries (`{dim, timeseries: [{date, value}]}`), single
 * points (`{dim, value}`), breakdowns (`{dim, breakdown: [...]}`) or
 * distributions (`{dim, distribution: [...]}`). Plain row arrays are kept
 * for tool-result / test inputs. Anything else digests as empty.
 */
function isMultiMetricResult(
  data: unknown
): data is { byMetric: Record<string, unknown>; fallback?: unknown } {
  return isRow(data) && isRow((data as Row).byMetric);
}

const MAP_LEVELS_COARSE_TO_FINE: readonly string[] = [
  LocationType.REGION,
  LocationType.COUNTRY,
  LocationType.STATE,
  LocationType.METRO_AREA,
  LocationType.COUNTY,
  LocationType.CITY,
];

/**
 * The places a map's digest names. A map payload carries every granularity at
 * once (the map paints each at its own zoom): talent discovery returns world
 * regions, countries, states, metros and cities in one response, so ranking
 * them together named "North America, United States, Southern Asia, India,
 * San Francisco" — the same people counted at several levels (live, stage
 * 2026-10-12). Digest one level: the coarsest with at least two places, which
 * is the first level the map can compare (one country is the scope, not a
 * comparison). A point with no map level (talent discovery's region rows map
 * to none) is a place the map never paints, so it is never named.
 */
function mapPointRows(points: Row[]): Row[] {
  const byLevel = new Map<string, Row[]>();
  for (const point of points) {
    if (
      typeof point.locationType !== 'string' ||
      !MAP_LEVELS_COARSE_TO_FINE.includes(point.locationType)
    ) {
      continue;
    }
    const level = byLevel.get(point.locationType) ?? [];
    level.push(point);
    byLevel.set(point.locationType, level);
  }
  const levels = MAP_LEVELS_COARSE_TO_FINE.map((level) => byLevel.get(level));
  return (
    levels.find((rows) => rows !== undefined && rows.length >= 2) ??
    levels.find((rows) => rows !== undefined) ??
    []
  );
}

function toChartShape(data: unknown, metric?: string): ChartShape {
  // Multi-metric providers (table charts always; gauge / y-bounded line via
  // `needsMultiMetricData`) emit `{ byMetric: Record<metric, DataProviderSet>,
  // fallback?: DataProviderSet }`, NOT the bare `{ type, data: [...] }`
  // envelope. Unwrap to the active metric's DataProviderSet (else `fallback`,
  // else the first populated metric) before the envelope checks below —
  // otherwise a widget that rendered real data digests as empty and the model
  // narrates "no data" for a chart that has data.
  if (isMultiMetricResult(data)) {
    const { byMetric } = data;
    const selected =
      (metric !== undefined ? byMetric[metric] : undefined) ??
      data.fallback ??
      Object.values(byMetric).find((set) => set !== undefined);
    return toChartShape(selected);
  }
  if (Array.isArray(data)) {
    return { rows: rowArray(data) };
  }
  // Map providers emit `{ points: MapPoint[] }`, one labelled value per place.
  // A point's `displayValue`, when set, is the figure itself and `value` the
  // rescaled one the map paints (a log or density view), so name the figure.
  if (isRow(data) && Array.isArray(data.points)) {
    const rows = mapPointRows(rowArray(data.points)).map((point) =>
      typeof point.displayValue === 'number'
        ? { ...point, value: point.displayValue }
        : point
    );
    return { rows, places: true };
  }
  if (!isRow(data) || !Array.isArray(data.data)) {
    return { rows: [] };
  }
  const items = rowArray(data.data);
  if (items.length === 0) return { rows: [] };

  const first = items[0];
  if (first && Array.isArray(first.timeseries)) {
    const primary =
      items.find((item) => item.seriesType === 'primary') ?? first;
    const rows = rowArray(primary.timeseries);
    if (items.length === 1) return { rows };
    const anchorDate = anchorDateFor(
      items.map((item) => rowArray(item.timeseries))
    );
    const seriesLatest = items
      .map((item) => {
        const series = rowArray(item.timeseries);
        // Same period for every category — see valueAtDate.
        const latest =
          anchorDate === undefined
            ? lastNumericValue(series)
            : valueAtDate(series, anchorDate);
        if (latest === undefined) return undefined;
        const first = firstNumericValue(series);
        const id =
          typeof item.id === 'string' || typeof item.id === 'number'
            ? item.id
            : undefined;
        return {
          label: dimLabel(item),
          value: latest,
          ...(first !== undefined && { first }),
          ...(id !== undefined && { id }),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
    return { rows, seriesLatest, anchorDate, seriesCount: items.length };
  }
  if (first && Array.isArray(first.breakdown) && isBoxBreakdown(items)) {
    return { rows: boxMedianRows(items) };
  }
  if (first && Array.isArray(first.breakdown)) {
    // Several series each holding their own points = a breakdown OVER TIME.
    // Return the primary series' points AND one labelled latest-value entry
    // per series, exactly as the `timeseries` branch above does. Returning
    // only `first.breakdown` left the digest with one unlabelled series and
    // no topCategories, so narration named it from the user's prompt ("the
    // US") rather than the data ("North America") — live 2026-08-26.
    const rows = rowArray(first.breakdown);
    if (items.length === 1) return { rows };
    const anchorDate = anchorDateFor(
      items.map((item) => rowArray(item.breakdown))
    );
    const seriesLatest = items
      .map((item) => {
        const series = rowArray(item.breakdown);
        // Same period for every category — see valueAtDate.
        const latest =
          anchorDate === undefined
            ? lastNumericValue(series)
            : valueAtDate(series, anchorDate);
        if (latest === undefined) return undefined;
        const firstValue = firstNumericValue(series);
        const id =
          typeof item.id === 'string' || typeof item.id === 'number'
            ? item.id
            : undefined;
        return {
          label: dimLabel(item),
          value: latest,
          ...(firstValue !== undefined && { first: firstValue }),
          ...(id !== undefined && { id }),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
    return { rows, seriesLatest, anchorDate, seriesCount: items.length };
  }
  if (first && Array.isArray(first.distribution)) {
    return { rows: items.flatMap(distributionMedianRow) };
  }
  // SingleDataPoint items ({dim, value}) — categorical.
  return { rows: items };
}

/** Box data: segments whose points carry a `box` of quantiles, no `value`. */
function isBoxBreakdown(items: Row[]): boolean {
  return items.some((item) =>
    rowArray(item.breakdown).some((point) => isRow(point.box))
  );
}

/**
 * One categorical row per box at its MEDIAN — the figure the box's middle line
 * paints and the one a "pay by role" answer quotes. Pay boxes are grouped by
 * segment with one box per entity inside, so a single entity names its rows
 * by segment alone; with several entities each row names both.
 */
function boxMedianRows(items: Row[]): Row[] {
  const entityLabels = new Set(
    items.flatMap((item) => rowArray(item.breakdown).map(dimLabel))
  );
  return items.flatMap((item) =>
    rowArray(item.breakdown).flatMap((point) => {
      const box = point.box;
      const median = isRow(box) ? box.q50 : undefined;
      if (typeof median !== 'number' || !Number.isFinite(median)) return [];
      const segment = dimLabel(item);
      const entity = dimLabel(point);
      const label =
        entityLabels.size <= 1 || entity === segment
          ? segment
          : `${segment} (${entity})`;
      return [
        {
          dim: label,
          value: median,
          ...((typeof item.id === 'string' || typeof item.id === 'number') && {
            id: item.id,
          }),
        },
      ];
    })
  );
}

/**
 * One row per distribution at its median, carrying its quartiles. The
 * histogram bins are not categories: digested as rows they told the model a
 * chart of one company held "656 salary observations".
 */
function distributionMedianRow(item: Row): Row[] {
  const points = rowArray(item.distribution);
  const percentiles = isRow(item.percentiles) ? item.percentiles : {};
  const quantile = (key: string, q: number) => {
    const given = percentiles[key];
    return typeof given === 'number' && Number.isFinite(given)
      ? given
      : quantileFromCdf(points, q);
  };
  const median = quantile('p50', 0.5);
  if (median === undefined) return [];
  return [
    {
      dim: dimLabel(item),
      value: median,
      p25: quantile('p25', 0.25),
      p75: quantile('p75', 0.75),
      ...((typeof item.id === 'string' || typeof item.id === 'number') && {
        id: item.id,
      }),
    },
  ];
}

/** The first bin whose cumulative share reaches `q`. */
function quantileFromCdf(points: Row[], q: number): number | undefined {
  const bin = points.find(
    (point) =>
      typeof point.cdf === 'number' &&
      point.cdf >= q &&
      typeof point.value === 'number'
  );
  return typeof bin?.value === 'number' ? bin.value : undefined;
}

function numericValues(rows: Row[]): number[] {
  // The single numeric series: prefer a `value` key; otherwise the first
  // numeric field found per row.
  return rows
    .map((row) => {
      if (typeof row.value === 'number') return row.value;
      const firstNumeric = Object.values(row).find(
        (v): v is number => typeof v === 'number'
      );
      return firstNumeric;
    })
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
}

function labelOf(row: Row): string | undefined {
  const candidate =
    row.label ?? row.dim ?? row.name ?? row.category ?? row.date;
  return typeof candidate === 'string' ? candidate : undefined;
}

function isTimeSeries(rows: Row[]): boolean {
  return rows.some((row) => typeof row.date === 'string');
}

function metricFormat(metric: string): FormatType {
  return getFormat({ metric: metric as MetricValues });
}

/** Salary and time to fill from a weighted request keep their own units. */
const POSTING_COUNT_METRICS: ReadonlySet<PostingMetricValues> = new Set([
  'postings_active',
  'postings_new',
  'postings_removed',
  'postings_volume',
]);

const isPostingCountMetric = (metric: string): boolean =>
  POSTING_COUNT_METRICS.has(metric as PostingMetricValues);

function isPercentageMetric(metric: string): boolean {
  return metricFormat(metric) === FormatType.PERCENTAGE;
}

function formatForNarration(
  metric: string,
  value: number,
  currencyCode?: string
): string | undefined {
  const format = metricFormat(metric);
  // Every FormatType `getFormatter` supports is safe to narrate — the model
  // reads `latestValueDisplay` verbatim, so a bare number here becomes a
  // unit-less figure in the answer (a salary with no currency, a count with
  // no separators). RATIO is the one exception: the product itself never
  // shows the raw ratio, so leaving it undefined keeps that metric out of
  // narration rather than inviting "0.01".
  if (format === FormatType.RATIO) return undefined;
  return getFormatter(format, currencyCode ? { currencyCode } : {})(value);
}

function formatChangeForNarration(
  metric: string,
  rawFirst: number | undefined,
  rawLatest: number | undefined
): string | undefined {
  if (
    !isPercentageMetric(metric) ||
    rawFirst === undefined ||
    rawLatest === undefined
  ) {
    return undefined;
  }
  return getFormatter(FormatType.PERCENTAGE_POINT)(
    rawLatest - rawFirst
  ).replace('pp', 'percentage points');
}

function categoryForNarration(
  metric: string,
  label: string,
  rawValue: number | undefined,
  value: number,
  currencyCode?: string
): DigestCategory {
  const displayValue =
    rawValue === undefined
      ? undefined
      : formatForNarration(metric, rawValue, currencyCode);
  return displayValue === undefined
    ? { label, value }
    : { label, value, displayValue };
}

/** Round to a narration-friendly precision (no fractional headcounts). */
function roundForNarration(
  value: number | undefined,
  metric?: string
): number | undefined {
  if (value === undefined) return undefined;
  if (metric !== undefined && isPercentageMetric(metric)) {
    return Math.round(value * 10000) / 10000;
  }
  return Math.abs(value) >= 100
    ? Math.round(value)
    : Math.round(value * 100) / 100;
}

/**
 * Remap a breakdown-timeseries envelope so each point's `value` becomes its
 * `share`, for charts whose axis DISPLAYS shares (see
 * `isHeadcountShareDisplayedOnLine`). Returns `undefined` when the payload
 * carries no share fields at all — the caller then digests the raw counts
 * rather than reporting an empty (or count-mislabeled-as-percent) digest for a
 * provider that never computed shares.
 */
function remapEnvelopeToShares(data: unknown): unknown | undefined {
  if (!isRow(data) || !Array.isArray(data.data)) return undefined;
  let sawShare = false;
  const mapPoints = (points: unknown[]): unknown[] =>
    points.map((point) => {
      if (!isRow(point)) return point;
      const share = point.share;
      if (typeof share === 'number' && Number.isFinite(share)) {
        sawShare = true;
        return { ...point, value: share };
      }
      // No share on this point (e.g. a zero-total period): drop the count so
      // it can't be digested as if it were a share.
      return { ...point, value: undefined };
    });
  const mapped = data.data.map((entry) => {
    if (!isRow(entry)) return entry;
    // A series' points live under `timeseries` on TIMESERIES payloads and
    // under `breakdown` on BREAKDOWN_TIMESERIES ones (what a composition
    // breakdown-over-time chart emits). Inspecting only `timeseries` meant a
    // share-displayed geography breakdown digested raw counts while its axis
    // painted percentages — live 2026-08-26.
    if (Array.isArray(entry.timeseries)) {
      return { ...entry, timeseries: mapPoints(entry.timeseries) };
    }
    if (Array.isArray(entry.breakdown)) {
      return { ...entry, breakdown: mapPoints(entry.breakdown) };
    }
    return entry;
  });
  return sawShare ? { ...data, data: mapped } : undefined;
}

export function buildChartDataDigest(params: {
  data: unknown;
  metric: string;
  chartType: string;
  title?: string;
  currencyCode?: string;
  /**
   * The plot's breakdown dimension, when it has one. Drives the share-display
   * decision below — omit it and a share-displaying breakdown line digests as
   * raw counts (chart axis "51%", narration "25,957").
   */
  secondaryDimension?: string;
  /** The plot's explicit display mode, when set (overrides the default). */
  displayAs?: 'value' | 'share' | 'auto';
  /** From the request sent; only a posting-count metric carries it. */
  postingsWeighting?: 'expected_hires';
}): ChartDataDigest {
  // A breakdown line over headcount DISPLAYS shares (Line.tsx paints
  // `t.share`) — digest the same field the chart paints, under the metric
  // that formats it as a percentage. Falls back to counts when the payload
  // has no share fields.
  const shareRemappedData = isHeadcountShareDisplayedOnLine({
    chartType: params.chartType,
    metric: params.metric,
    secondaryDimension: params.secondaryDimension,
    displayAs: params.displayAs,
  })
    ? remapEnvelopeToShares(params.data)
    : undefined;
  const effectiveMetric =
    shareRemappedData !== undefined ? 'share_of_headcount' : params.metric;
  const effectiveData = shareRemappedData ?? params.data;

  const { rows, seriesLatest, anchorDate, places, seriesCount } = toChartShape(
    effectiveData,
    effectiveMetric
  );
  const values = numericValues(rows);
  const empty = rows.length === 0 || values.length === 0;

  let latestValue: number | undefined;
  let latestValueDisplay: string | undefined;
  let firstValue: number | undefined;
  let firstValueDisplay: string | undefined;
  let peakValue: number | undefined;
  let peakValueDisplay: string | undefined;
  let peakDate: string | undefined;
  let deltaPct: number | undefined;
  let changePpDisplay: string | undefined;
  let firstDate: string | undefined;
  let lastDate: string | undefined;
  let topCategories: DigestCategory[] | undefined;
  let bottomCategories: DigestCategory[] | undefined;
  let categoryCount: number | undefined;

  if (!empty) {
    if (isTimeSeries(rows)) {
      // Derive latest/first from the same rows that carry a finite value, so
      // `latestValue` and `lastDate` describe the SAME (last complete) data
      // point. Taking `lastDate` from the raw `dates` array would point at a
      // trailing incomplete period whose value is null, mismatching the last
      // finite `latestValue`.
      const finiteRows = rows.filter(
        (row) =>
          typeof row.value === 'number' && Number.isFinite(row.value as number)
      );
      // ONE period for the whole digest. `rows` is the PRIMARY series, which
      // on a breakdown-over-time is an arbitrary category — so its own last
      // point can predate the chart's right edge, and reporting it as
      // `lastDate` beside categories read at the anchor narrates two different
      // months as one (Beyza P2 on #6500).
      //
      // When the primary reaches the anchor, that row is the headline. When it
      // does not, the headline is DROPPED rather than back-dated: a stale
      // figure under the chart's latest date is the same "stale narrated as
      // current" failure `valueAtDate` exists to prevent, one level up. A
      // multi-series chart still carries `topCategories` at the anchor, which
      // is where a breakdown's answer lives anyway.
      const anchoredRow =
        anchorDate === undefined
          ? finiteRows[finiteRows.length - 1]
          : finiteRows.find((row) => row.date === anchorDate);
      const primaryReachesAnchor = anchoredRow !== undefined;
      const lastCompleteRow = anchoredRow;
      const firstCompleteRow = primaryReachesAnchor ? finiteRows[0] : undefined;
      const rawLatest =
        typeof lastCompleteRow?.value === 'number'
          ? lastCompleteRow.value
          : undefined;
      const rawFirst =
        typeof firstCompleteRow?.value === 'number'
          ? firstCompleteRow.value
          : undefined;
      latestValue = roundForNarration(rawLatest, effectiveMetric);
      firstValue = roundForNarration(rawFirst, effectiveMetric);
      latestValueDisplay =
        rawLatest === undefined
          ? undefined
          : formatForNarration(effectiveMetric, rawLatest, params.currencyCode);
      firstValueDisplay =
        rawFirst === undefined
          ? undefined
          : formatForNarration(effectiveMetric, rawFirst, params.currencyCode);
      // Peak of the primary series, so narration can anchor on the visible high
      // point rather than the (often spiky) first point.
      let peakRow = finiteRows[0];
      for (const row of finiteRows) {
        if (
          typeof row.value === 'number' &&
          (peakRow === undefined ||
            typeof peakRow.value !== 'number' ||
            row.value > peakRow.value)
        ) {
          peakRow = row;
        }
      }
      if (peakRow && typeof peakRow.value === 'number') {
        peakValue = roundForNarration(peakRow.value, effectiveMetric);
        peakValueDisplay = formatForNarration(
          effectiveMetric,
          peakRow.value,
          params.currencyCode
        );
        peakDate = typeof peakRow.date === 'string' ? peakRow.date : undefined;
      }
      // Compute the delta from the RAW first/latest values; feeding the
      // already-rounded `latestValue` in here would compound rounding error.
      if (rawFirst !== undefined && rawFirst !== 0 && rawLatest !== undefined) {
        deltaPct =
          Math.round(((rawLatest - rawFirst) / Math.abs(rawFirst)) * 1000) / 10;
      }
      changePpDisplay = formatChangeForNarration(
        effectiveMetric,
        rawFirst,
        rawLatest
      );
      firstDate =
        typeof firstCompleteRow?.date === 'string'
          ? firstCompleteRow.date
          : undefined;
      // The anchor even when the primary did not reach it: the categories
      // below describe that period, and `lastDate` is what names it.
      lastDate =
        anchorDate ??
        (typeof lastCompleteRow?.date === 'string'
          ? lastCompleteRow.date
          : undefined);
      if (seriesLatest && seriesLatest.length > 1) {
        categoryCount = seriesLatest.length;
        topCategories = [...seriesLatest]
          .sort((a, b) => b.value - a.value)
          .slice(0, 5)
          .map((entry) => {
            const category = categoryForNarration(
              effectiveMetric,
              entry.label,
              entry.value,
              roundForNarration(entry.value, effectiveMetric) ?? entry.value,
              params.currencyCode
            );
            if (entry.id !== undefined) category.id = entry.id;
            if (entry.first !== undefined && entry.first !== 0) {
              // Same rounding contract as the primary-series deltaPct above.
              category.deltaPct =
                Math.round(
                  ((entry.value - entry.first) / Math.abs(entry.first)) * 1000
                ) / 10;
            }
            return category;
          });
      }
    } else {
      const labeled = rows
        .map((row) => {
          const label = labelOf(row);
          const rawValue =
            typeof row.value === 'number' ? row.value : numericValues([row])[0];
          // Round like every other narrated figure — raw FP arithmetic leaks
          // "-0.41000000000000003" straight into the user-facing answer (the
          // model quotes digest values verbatim, seen live on prod 2026-07-08).
          const value = roundForNarration(rawValue, effectiveMetric);
          if (label === undefined || value === undefined) return undefined;
          const category = categoryForNarration(
            effectiveMetric,
            label,
            rawValue,
            value,
            params.currencyCode
          );
          if (typeof row.id === 'string' || typeof row.id === 'number') {
            category.id = row.id;
          }
          if (typeof row.p25 === 'number' && typeof row.p75 === 'number') {
            const low = formatForNarration(
              effectiveMetric,
              row.p25,
              params.currencyCode
            );
            const high = formatForNarration(
              effectiveMetric,
              row.p75,
              params.currencyCode
            );
            if (low !== undefined && high !== undefined) {
              category.middleHalfDisplay = `${low}–${high}`;
            }
          }
          // Size context for rate breakdowns (top-segment growth families):
          // the plotted value is the rate, this is how big the segment is.
          if (
            typeof row.segmentHeadcount === 'number' &&
            Number.isFinite(row.segmentHeadcount)
          ) {
            category.segmentHeadcount = Math.round(row.segmentHeadcount);
            // Plain grouped count — `formatForNarration` formats `row.value`
            // in the CHART's metric format (e.g. a rate), which is the wrong
            // format for this sibling headcount field, so it's not reused here.
            category.segmentHeadcountDisplay =
              category.segmentHeadcount.toLocaleString('en-US');
          }
          // Native share of the classified breakdown total, when the provider
          // payload carries one (BREAKDOWN_SINGLE rows and a one-entity
          // segment bar do; a bar summed across entities has no single
          // denominator). See `DigestCategory.shareDisplay`. A share is
          // a proportion, so a value outside 0..1 is not one: the total-spend
          // provider stores each segment's payroll amount in `share`.
          if (
            typeof row.share === 'number' &&
            Number.isFinite(row.share) &&
            row.share >= 0 &&
            row.share <= 1
          ) {
            const shareDisplay = formatForNarration(
              'share_of_headcount',
              row.share
            );
            if (shareDisplay !== undefined) {
              category.shareDisplay = shareDisplay;
            }
          }
          return category;
        })
        .filter((entry): entry is DigestCategory => Boolean(entry))
        .sort((a, b) => b.value - a.value);
      if (labeled.length > 0) {
        categoryCount = labeled.length;
        topCategories = labeled.slice(0, 5);
        // A map's places are named, never totalled: its largest place is not
        // the chart's figure, and its smallest are not a "worst end".
        if (!places) {
          latestValue = labeled[0]?.value;
          latestValueDisplay = labeled[0]?.displayValue;
          // Worst end (worst first), only when it adds rows topCategories
          // doesn't already show — a breakdown answer cites the salient end
          // (best or worst) depending on the question, so both must be present
          // for wide breakdowns like the 20-aspect sentiment_top_bottom set.
          if (labeled.length > 5) {
            bottomCategories = labeled.slice(-5).reverse();
          }
        }
      }
    }
  }

  const countsWeightedPostings =
    params.postingsWeighting === 'expected_hires' &&
    isPostingCountMetric(effectiveMetric);

  const digest: ChartDataDigest = {
    kind: 'chart_data',
    title: params.title,
    metric: effectiveMetric,
    // Label the metric actually DIGESTED, not the one requested: a share remap
    // rewrites `headcount` to `share_of_headcount`, and labelling the request
    // would name the chart something it no longer is.
    metricLabel: countsWeightedPostings
      ? `${narrationMetricLabel(effectiveMetric)} weighted by expected hires`
      : narrationMetricLabel(effectiveMetric),
    ...(countsWeightedPostings ? { postingsWeighting: 'expected_hires' } : {}),
    chartType: params.chartType,
    // A map's rows are its named places at one level (see `mapPointRows`),
    // never the payload's overlapping places at every level.
    rowCount: rows.length,
    latestValue,
    firstValue,
    peakValue,
    peakDate,
    deltaPct,
    firstDate,
    lastDate,
    topCategories,
    bottomCategories,
    empty,
  };
  if (categoryCount !== undefined) {
    digest.categoryCount = categoryCount;
  }
  if (!empty && isTimeSeries(rows) && (seriesCount ?? 1) <= 1) {
    digest.singleSeries = true;
  }
  if (
    isRow(params.data) &&
    params.data['type'] === DataType.SINGLE &&
    !isPercentageMetric(effectiveMetric) &&
    topCategories?.some((category) => category.shareDisplay !== undefined)
  ) {
    digest.sharesBesideCounts = true;
  }
  if (latestValueDisplay !== undefined) {
    digest.latestValueDisplay = latestValueDisplay;
  }
  if (firstValueDisplay !== undefined) {
    digest.firstValueDisplay = firstValueDisplay;
  }
  if (peakValueDisplay !== undefined) {
    digest.peakValueDisplay = peakValueDisplay;
  }
  if (changePpDisplay !== undefined) {
    digest.changePpDisplay = changePpDisplay;
  }
  return digest;
}

/**
 * The resolved talent-discovery dimensions other than company/school live ONLY
 * inside `filters.expr` (a `UserNode` expression tree) — the final
 * `TalentDiscoveryAiFilterSearchResponseV2` does NOT keep per-dimension named
 * lists for gender/geography/role/etc. Each leaf value carries a `kind` enum
 * (`UserCategoryKind` / `PositionCategoryKind`) identifying its dimension but
 * only id-only values (no human name). This maps each `kind` to the dimension
 * `filterId` we narrate. `COMPANY_ID` / `RSID` are intentionally excluded —
 * those are already reported from `companyNames` / `schoolNames`.
 */
const EXPR_KIND_TO_FILTER_ID: Record<string, string> = {
  // UserCategoryKind
  GENDER: 'gender',
  ETHNICITY: 'ethnicity',
  HIGHEST_DEGREE: 'education',
  SKILL_LVL_1: 'skill',
  SKILL_LVL_2: 'skill',
  SKILL_LVL_3: 'skill',
  SKILL_LVL_4: 'skill',
  SKILL_LVL_5: 'skill',
  USER_CITY: 'geography',
  USER_COUNTRY: 'geography',
  USER_MSA: 'geography',
  USER_REGION: 'geography',
  USER_STATE: 'geography',
  USER_COUNTY: 'geography',
  // PositionCategoryKind
  CITY: 'geography',
  COUNTRY: 'geography',
  MSA: 'geography',
  REGION: 'geography',
  STATE: 'geography',
  COUNTY: 'geography',
  CONTINGENT: 'contingent',
  FLIGHT_RISK: 'flight_risk',
  PRESTIGE: 'prestige',
  REMOTE_SUITABILITY: 'remote_suitability',
  EMPLOYMENT_RELATIONSHIP: 'employment_relationship',
  WORK_SCHEDULE: 'work_schedule',
  RICS_K10: 'industry',
  RICS_K50: 'industry',
  RICS_K400: 'industry',
  ROLE_K7: 'role',
  ROLE_K150: 'role',
  ROLE_K1500: 'role',
  ROLE_K15000: 'role',
  ROLE_K17000: 'role',
  SENIORITY: 'seniority',
  // PositionTextKind / range kinds
  TITLE: 'role',
  SALARY_BASE: 'salary',
  SALARY_TOTAL: 'salary',
  GRADUATION_YEAR: 'graduation_year',
  YEARS_OF_EXP: 'years_of_experience',
};

/**
 * Dimensions identified by their leaf KEY rather than a `kind` tag. Activity
 * rides a dedicated `pos_activity` node with no `kind` field, so the
 * kind-based lookup is structurally blind to it.
 */
const EXPR_LEAF_KEY_TO_FILTER_ID: Record<string, string> = {
  pos_activity: 'activity',
};

type ExprDimensionLeaf = {
  /** `undefined` for company/school and for kinds outside the allowlist. */
  filterId: string | undefined;
  kind: string | undefined;
  attr: Row;
  /** Under a `not` / `user_not`: the resolver's `polarity: 'exclude'`. */
  negated: boolean;
  /** An `rng` leaf: a numeric range such as years of experience. */
  range: boolean;
};

/**
 * Report every leaf attribute of a `UserNode` expr tree, read defensively.
 * ANY negated ancestor marks a leaf negated, as dash-meta's `walkExprLeaves`
 * does.
 */
function visitExprDimensionLeaves(
  expr: unknown,
  onLeaf: (leaf: ExprDimensionLeaf) => void
): void {
  const visitExprNode = (node: unknown, negated: boolean): void => {
    if (!isRow(node)) return;
    if (isRow(node.and)) {
      visitExprNode((node.and as Row).child ?? node.and, negated);
    }
    if (isRow(node.not)) visitExprNode((node.not as Row).child, true);
    if (isRow(node.or)) {
      for (const child of rowArray((node.or as Row).children)) {
        visitExprNode(child, negated);
      }
    }
    for (const leafKey of ['val', 'rng', 'txt'] as const) {
      const leaf = node[leafKey];
      if (!isRow(leaf)) continue;
      const entries = Object.entries(leaf);
      const hasKindBearingSibling = entries.some(
        ([, attr]) => isRow(attr) && typeof attr.kind === 'string'
      );
      for (const [attrKey, attr] of entries) {
        if (!isRow(attr)) continue;
        // The kindless twin the resolver dual-sends beside a role or skill
        // leaf: counted once, by its kind-bearing sibling.
        if (DUAL_SEND_SLOTS.has(attrKey) && hasKindBearingSibling) continue;
        const kind = typeof attr.kind === 'string' ? attr.kind : undefined;
        onLeaf({
          filterId:
            EXPR_LEAF_KEY_TO_FILTER_ID[attrKey] ??
            (kind ? EXPR_KIND_TO_FILTER_ID[kind] : undefined),
          kind,
          attr,
          negated,
          range: leafKey === 'rng',
        });
      }
    }
    // ExprAndNode uses `children`; ExprOrNode uses `children`; some shapes
    // nest the boolean group's children directly on the node.
    for (const child of rowArray(node.children)) {
      visitExprNode(child, negated);
    }
  };

  const visitUserNode = (node: unknown, negated: boolean): void => {
    if (!isRow(node)) return;
    if (isRow(node.user_and)) {
      for (const child of rowArray((node.user_and as Row).children)) {
        visitUserNode(child, negated);
      }
    }
    if (isRow(node.user_or)) {
      for (const child of rowArray((node.user_or as Row).children)) {
        visitUserNode(child, negated);
      }
    }
    if (isRow(node.user_not)) {
      visitUserNode((node.user_not as Row).child, true);
    }
    if (isRow(node.user_value)) {
      visitExprNode((node.user_value as Row).child, negated);
    }
  };

  visitUserNode(expr, false);
}

/**
 * Walk a `UserNode` expr tree and collect the set of dimension `filterId`s
 * present, in first-seen order. Company/school kinds are skipped (reported
 * separately from the named lists). Includes exclusions: they did resolve,
 * even where a page cannot carry them.
 */
function collectExprFilterIds(expr: unknown): string[] {
  const ordered: string[] = [];
  visitExprDimensionLeaves(expr, ({ filterId }) => {
    if (filterId && !ordered.includes(filterId)) ordered.push(filterId);
  });
  return ordered;
}

/** Which named-list field on the V2 response carries a dimension's resolved nodes. */
const RESOLVED_NAME_FIELD_BY_FILTER_ID: Partial<
  Record<
    string,
    'roleNames' | 'skillNames' | 'activityNames' | 'geographyNames'
  >
> = {
  role: 'roleNames',
  skill: 'skillNames',
  activity: 'activityNames',
  geography: 'geographyNames',
};

/**
 * Human name(s) for a dimension that resolved into the expr tree, when the
 * response carries them (currently role/skill/activity only — see `roleNames`/
 * `skillNames`/`activityNames` on
 * response carries them (role/skill/geography — see `roleNames`/
 * `skillNames`/`geographyNames` on
 * `TalentDiscoveryAiFilterSearchResponseV2`). Multiple
 * resolved nodes for the same dimension are joined so the digest still
 * reports the dimension once. Returns `null` when no names are available
 * (unsupported dimension, or an older/cached response predating the field)
 * so the caller can fall back to the lossy id-only shape.
 */
function resolvedDimensionLabel(
  filterId: string,
  response: TalentDiscoveryAiFilterSearchResponseV2
): string | null {
  const field = RESOLVED_NAME_FIELD_BY_FILTER_ID[filterId];
  if (!field) return null;
  const names = response.filters[field];
  if (!names || names.length === 0) return null;
  const unique = Array.from(
    new Set(names.map((entry) => entry.name).filter((name) => name.length > 0))
  );
  if (unique.length === 0) return null;
  const joined = unique.join(', ');
  // Several nodes for one dimension are a UNION that is already applied, not a
  // set of candidates — see MULTI_NODE_QUALIFIER in the server-side summary
  // (turn-server-tools.summaries.ts), whose wording this matches. Unqualified,
  // the list reads as a menu and the model asks the user to choose something
  // it has already decided.
  return unique.length > 1
    ? `${joined} — all applied together as one filter`
    : joined;
}

export function buildFilterResolutionDigest(params: {
  response: TalentDiscoveryAiFilterSearchResponseV2;
  targetPage: string;
}): FilterResolutionDigest {
  const { response, targetPage } = params;
  const applied: FilterResolutionDigest['applied'] = [];

  for (const company of response.filters.companyNames ?? []) {
    applied.push({
      // Resolution requests don't echo per-entity request text; pair the
      // resolved name with the valid prompt fragment when there is exactly
      // one, otherwise fall back to the resolved name itself.
      requested:
        response.relevantPromptText.valid.length === 1
          ? (response.relevantPromptText.valid[0] ?? company.name)
          : company.name,
      resolvedTo: company.name,
      filterId: 'company',
    });
  }
  for (const school of response.filters.schoolNames ?? []) {
    applied.push({
      requested: school.name,
      resolvedTo: school.name,
      filterId: 'school',
    });
  }

  // A date-only apply must not report no resolved filters after it was queued.
  const dateRange = response.date_range;
  const dateLabel = formatIsoDayRange(
    dateRange?.start_value,
    dateRange?.end_value
  );
  if (dateLabel) {
    applied.push({
      requested: dateRange?.relevantPromptText || dateLabel,
      resolvedTo: dateLabel,
      filterId: 'date range',
    });
  }

  // Posting source and overall rating sit at the response ROOT, not in the
  // expr tree, so neither loop above sees them. Without this a source-only or
  // rating-only resolution reported nothing applied. Mirrors
  // ROOT_STATIC_LIST_DIMENSIONS in the dash-meta summary builder.
  // `||`, never `??`: `relevantPromptText` is a required string, and
  // `buildDerivedFilterArtifact` writes it EMPTY for a source the read minted
  // rather than the user named — `?? ` keeps that empty string and the applied
  // line renders as " -> LinkedIn (posting source)" with nothing before the
  // arrow.
  for (const source of response.posting_source ?? []) {
    if (!source?.name) continue;
    applied.push({
      requested: source.relevantPromptText || source.name,
      resolvedTo: source.name,
      filterId: 'posting source',
    });
  }
  for (const rating of response.overall_rating ?? []) {
    if (!rating?.name) continue;
    applied.push({
      requested: rating.relevantPromptText || rating.name,
      resolvedTo: rating.name,
      filterId: 'overall rating',
    });
  }

  // Non-company/school dimensions (gender, geography, role, seniority, …)
  // resolve into the expr tree with no per-dimension human name on the V2
  // response. Report each DISTINCT dimension once. We deliberately do NOT pair
  // the prompt fragments (`relevantPromptText.valid`) to dimensions positionally
  // — the fragment count rarely matches the dimension count, so index-pairing
  // mislabeled values (e.g. reported "California" as the gender filter, making
  // the model claim a filter "resolved twice"). The dimension list is accurate;
  // the model has the user's prompt for the specific values.
  //
  // Exception: role, skill and activity DO carry a resolved human name on the
  // response (`roleNames`/`skillNames`/`activityNames` — the taxonomy tree they
  // resolved against is known only server-side, so the name is correct by
  // construction there; a
  // client-side lookup here could hit the wrong tree, see transformValue.ts).
  // When present, use it as `resolvedTo` so the model narrates what was
  // actually applied (e.g. "Ground Operations") instead of the dimension id.
  // Older/cached responses without the field fall back to the id, unchanged.
  for (const filterId of collectExprFilterIds(response.filters.expr)) {
    applied.push({
      requested: filterId,
      resolvedTo: resolvedDimensionLabel(filterId, response) ?? filterId,
      filterId,
    });
  }

  const unresolved: FilterResolutionDigest['unresolved'] = [];
  for (const unknown of response.unknownFilters ?? []) {
    const requested =
      typeof (unknown as Row).name === 'string'
        ? String((unknown as Row).name)
        : JSON.stringify(unknown);
    // The model narrates whatever reason it is given, so an exclusion that
    // could not be applied must not reach it as "unsupported filter".
    const explanation = (unknown as Row).explanation;
    unresolved.push({
      requested,
      reason:
        typeof explanation === 'string' && explanation.length > 0
          ? explanation
          : 'unsupported filter',
    });
  }
  for (const invalid of response.relevantPromptText.invalid ?? []) {
    if (!unresolved.some((entry) => entry.requested === invalid)) {
      unresolved.push({ requested: invalid });
    }
  }

  return { kind: 'filter_resolution', applied, unresolved, targetPage };
}

/**
 * Named list fields on `company_info_selectors` whose resolved entries carry a
 * `.name` string we surface in APPLIED. The `filterId` is the field name.
 */
const COMPANY_INFO_NAME_FIELDS = [
  'competitors_of',
  'hq_geography',
  'funding_stage',
  'founder_prev_company',
  'founder_prev_university',
  'founder_gender',
  'founder_ethnicity',
] as const;

/** Segment metric keys present in `CompanyDiscoveryAiOutput.filters.segments`. */
const SEGMENT_METRIC_KEYS = [
  'headcount',
  'inflow',
  'outflow',
  'hiring',
  'attrition',
  'growth',
  'growth_yoy',
  'salary',
  'tenure',
] as const;

/**
 * Build a `filter_resolution` digest from a screener `CompanyDiscoveryAiOutput`.
 *
 * Screener applies navigate + apply real filters but the artifact has no root
 * `relevantPromptText`, so the TD `buildFilterResolutionDigest` can't read it.
 * This mirrors the BE summary in
 * `apps/dash-meta/.../turn-server-tools.summaries.ts` (collectScreenerApplied)
 * so FE and BE report the same filters. Sources:
 *
 * 1. `filters.primary_selector.{industry,geography,role}[].name`
 *    → filterId 'industry' | 'geography' | 'role'
 * 2. `filters.entity_info_selectors.company_info_selectors.<field>[].name`
 *    for name-bearing list fields → filterId = the field name
 * 3. `filters.segments.<metric>[].relevantPromptText` → captures superlative
 *    phrases like "highest attrition"; filterId = the metric key
 * 4. `unknownFilters[].name` → unresolved ('unsupported filter')
 */
export function buildScreenerFilterResolutionDigest(params: {
  response: CompanyDiscoveryAiOutput;
  targetPage: string;
}): FilterResolutionDigest {
  const { response, targetPage } = params;
  const filters = response.filters;
  const applied: FilterResolutionDigest['applied'] = [];

  // Every field below is `... | null` on the real CompanyDiscoveryAiOutput, and
  // `name` / `relevantPromptText` can be absent on a resolved item (e.g. a
  // numeric-bound segment carries start/end values but no prompt phrase). Guard
  // every string access so a partial screener response never throws.
  const pushName = (name: unknown, filterId: string): void => {
    if (typeof name === 'string' && name.length > 0) {
      applied.push({ requested: name, resolvedTo: name, filterId });
    }
  };

  // 1. primary_selector.{industry,geography,role}
  const primarySelector = filters?.primary_selector;
  if (primarySelector) {
    for (const key of ['industry', 'geography', 'role'] as const) {
      for (const item of primarySelector[key] ?? []) {
        pushName(item?.name, key);
      }
    }
  }

  // 2. entity_info_selectors.company_info_selectors.<field>
  const companyInfoSelectors =
    filters?.entity_info_selectors?.company_info_selectors;
  if (companyInfoSelectors) {
    for (const field of COMPANY_INFO_NAME_FIELDS) {
      for (const item of companyInfoSelectors[field] ?? []) {
        pushName(item?.name, field);
      }
    }
  }

  // 3. segments.<metric>[]: prefer the resolved phrase (superlatives like
  // "highest attrition"); otherwise synthesize a label from the numeric bounds
  // (e.g. "attrition ≥ 80") so an applied threshold is still reported.
  const segments = filters?.segments;
  if (segments) {
    for (const metric of SEGMENT_METRIC_KEYS) {
      for (const item of segments[metric] ?? []) {
        const text = item?.relevantPromptText;
        if (typeof text === 'string' && text.length > 0) {
          pushName(text, metric);
          continue;
        }
        const boundParts: string[] = [];
        if (item?.start_value != null) {
          boundParts.push(`≥ ${item.start_value}`);
        }
        if (item?.end_value != null) {
          boundParts.push(`≤ ${item.end_value}`);
        }
        if (boundParts.length > 0) {
          pushName(`${metric} ${boundParts.join(' and ')}`, metric);
        }
      }
    }
  }

  // 4. unknownFilters[].name → unresolved
  const unresolved: FilterResolutionDigest['unresolved'] = [];
  for (const unknown of response.unknownFilters ?? []) {
    if (typeof unknown?.name === 'string' && unknown.name.length > 0) {
      unresolved.push({
        requested: unknown.name,
        reason: 'unsupported filter',
      });
    }
  }

  return { kind: 'filter_resolution', applied, unresolved, targetPage };
}

export function buildReportPrefillDigest(params: {
  reportName: string;
  suggestions: GlobalAssistantReportParamSuggestion[];
}): ReportPrefillDigest {
  const resolved: ReportPrefillDigest['resolved'] = [];
  const skipped: ReportPrefillDigest['skipped'] = [];
  for (const suggestion of params.suggestions) {
    if (
      suggestion.resolutionStatus === 'resolved' &&
      suggestion.resolvedSelection
    ) {
      resolved.push({
        parameter: suggestion.displayName,
        value: suggestion.resolvedSelection.name,
      });
    } else {
      skipped.push({
        parameter: suggestion.displayName,
        searchTerm: suggestion.searchTerm,
        reason: suggestion.resolutionReason ?? suggestion.resolutionStatus,
      });
    }
  }
  return {
    kind: 'report_prefill',
    reportName: params.reportName,
    resolved,
    skipped,
  };
}

/**
 * Summarize the demographic/entity filters a chart was ACTUALLY rendered with
 * into stable labels, reusing the same applied-filter extraction the filter
 * digest does (companyNames/schoolNames + the dimensions in the `expr` tree).
 * Company/school carry their resolved name (`company: Google LLC`); every other
 * dimension is reported by its bare id (`gender`, `ethnicity`, …) because the
 * V2 response keeps no per-dimension human name. Returns `[]` for an absent
 * payload. Surfaced into the chart digest so the grounded narration has GROUND
 * TRUTH that the filters applied and never claims one "did not resolve".
 * Exclusions are left out: no chart host carries one.
 */
export function summarizeAppliedChartFilters(
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined
): string[] {
  if (filters === undefined) return [];
  const { applied } = buildFilterResolutionDigest({
    response: filters,
    targetPage: 'chart',
  });
  const polarity = readChartFilterPolarity(filters);
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const entry of applied) {
    if (
      entry.filterId === 'company' &&
      polarity.excludedCompanyNames.has(entry.resolvedTo) &&
      !polarity.includedCompanyNames.has(entry.resolvedTo)
    ) {
      continue;
    }
    if (
      EXPR_FILTER_IDS.has(entry.filterId) &&
      !polarity.includedFilterIds.has(entry.filterId)
    ) {
      continue;
    }
    const label =
      entry.filterId === 'company' || entry.filterId === 'school'
        ? `${entry.filterId}: ${entry.resolvedTo}`
        : entry.filterId;
    if (!seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels;
}

/** Every `filterId` the expr walk can narrate a dimension as. */
const EXPR_FILTER_IDS: ReadonlySet<string> = new Set([
  ...Object.values(EXPR_KIND_TO_FILTER_ID),
  ...Object.values(EXPR_LEAF_KEY_TO_FILTER_ID),
]);

/**
 * Excluded-filter names for kinds with no dimension in `EXPR_KIND_TO_FILTER_ID`.
 * Mirrors dash-meta's `UNSUPPORTED_FILTER_PHRASES` (do NOT import across apps).
 */
const EXCLUDED_ONLY_KIND_LABELS: Readonly<Record<string, string>> = {
  DESC: 'keyword',
  NAME: 'person name',
};

/**
 * Kindless twins the resolver dual-sends beside a kind-bearing role or skill
 * leaf. Mirrors dash-meta's `DUAL_SEND_SLOTS` (do NOT import across apps).
 */
const DUAL_SEND_SLOTS: ReadonlySet<string> = new Set([
  'pos_role',
  'user_skill',
]);

type ChartFilterPolarity = {
  /** Dimensions with at least one INCLUDED leaf — what a chart applies. */
  includedFilterIds: Set<string>;
  includedCompanyNames: Set<string>;
  excludedCompanyNames: Set<string>;
  /** Labels of the excluded and range leaves, in first-seen order. */
  excludedLabels: string[];
};

/**
 * Companies are matched by their leaf's rcid: `companyNames` lists an excluded
 * company beside the included ones with nothing marking which is which.
 */
function readChartFilterPolarity(
  filters: TalentDiscoveryAiFilterSearchResponseV2
): ChartFilterPolarity {
  const nameByRcid = new Map(
    (filters.filters.companyNames ?? []).map((entry) => [
      entry.rcid,
      entry.name,
    ])
  );
  const polarity: ChartFilterPolarity = {
    includedFilterIds: new Set(),
    includedCompanyNames: new Set(),
    excludedCompanyNames: new Set(),
    excludedLabels: [],
  };
  visitExprDimensionLeaves(
    filters.filters.expr,
    ({ filterId, kind, attr, negated, range }) => {
      const companyName =
        kind === 'COMPANY_ID' && typeof attr.id === 'number'
          ? nameByRcid.get(attr.id)
          : undefined;
      // Only the people-search provider applies a numeric range, and the
      // assistant never charts from it.
      if (range && !negated) {
        const rangeLabel = `${filterId ?? 'filter'} (range)`;
        if (!polarity.excludedLabels.includes(rangeLabel)) {
          polarity.excludedLabels.push(rangeLabel);
        }
        return;
      }
      if (!negated) {
        if (filterId) polarity.includedFilterIds.add(filterId);
        if (companyName) polarity.includedCompanyNames.add(companyName);
        return;
      }
      let label: string;
      if (kind === 'COMPANY_ID') {
        if (companyName) polarity.excludedCompanyNames.add(companyName);
        label = companyName ? `company: ${companyName}` : 'company';
      } else if (kind === 'RSID') {
        label = 'school';
      } else {
        label =
          filterId ??
          (kind ? EXCLUDED_ONLY_KIND_LABELS[kind] : undefined) ??
          'filter';
      }
      const excludedLabel = `${label} (excluded)`;
      if (!polarity.excludedLabels.includes(excludedLabel)) {
        polarity.excludedLabels.push(excludedLabel);
      }
    }
  );
  return polarity;
}

/**
 * The filters a chart resolved but could not apply: every exclusion and every
 * numeric range. Inline charts and built pages are custom pages, where
 * `adaptAiSearchFiltersForPage` strips every `isNot` filter and negated
 * primary selection, and no workforce provider reads a range.
 */
export function summarizeUnappliedChartFilters(
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined
): string[] {
  if (filters === undefined) return [];
  return readChartFilterPolarity(filters).excludedLabels;
}

/**
 * A successfully-settled chart digest carries only the raw `metric` (e.g.
 * "headcount") with no record of the FILTER scope the chart was rendered with
 * (e.g. "Asian men at Google"). Narrating from that bare digest makes the model
 * describe the value as unfiltered and redundantly offer a filter that is
 * already applied on the chart. Carry the model-facing chart
 * description (intentSummary, else chartSubtitle) into the digest `title` AND
 * the explicit applied-filter labels into `filtersApplied`, so the grounded
 * narration knows the chart's scope and has structured proof the filters
 * applied, and the ones it could NOT apply into `filtersNotApplied`. An
 * existing title / filtersApplied / filtersNotApplied is preserved.
 */
export function withChartScope(
  digest: ChartDataDigest,
  scope: {
    intentSummary?: string;
    chartSubtitle?: string;
    filtersApplied?: string[];
    filtersNotApplied?: string[];
    /**
     * Passed, even as `undefined`, only by the `render_chart` paths, whose chart
     * model is asked to classify level vs share: there a missing value means
     * the classification never arrived, and it is stamped 'unclassified'. Page
     * builds leave the key out.
     */
    valueForm?: ChartValueForm | undefined;
  }
): ChartDataDigest {
  const title = digest.title ?? scope.intentSummary ?? scope.chartSubtitle;
  const filtersApplied =
    digest.filtersApplied ??
    (scope.filtersApplied && scope.filtersApplied.length > 0
      ? scope.filtersApplied
      : undefined);
  const filtersNotApplied =
    digest.filtersNotApplied ??
    (scope.filtersNotApplied && scope.filtersNotApplied.length > 0
      ? scope.filtersNotApplied
      : undefined);
  return {
    ...digest,
    ...(title ? { title } : {}),
    ...(filtersApplied ? { filtersApplied } : {}),
    ...(filtersNotApplied ? { filtersNotApplied } : {}),
    ...('valueForm' in scope
      ? { valueForm: scope.valueForm ?? 'unclassified' }
      : {}),
  };
}

// Model-facing digest serialization. Exported for the client-tool result copy
// (client-tools/tool-result-copy.ts) and the seeded-build landing narration
// (seeded-build-narration.ts), which seeds a narrate-only turn with the SAME
// digest contract lines the create_custom_page result uses — one phrasing, one
// grounding contract.
export function chartSummary(d: ChartDataDigest): string {
  // Never `d.metric`: everything below is text the model reads and paraphrases,
  // and told to "lead with the concrete figures above" it copies whatever names
  // the metric — see `metricLabel`'s docblock for what that shipped to a user.
  const metricName = d.metricLabel ?? narrationMetricLabel(d.metric);
  if (d.unsettled) {
    // The chart never finished loading — its data is UNKNOWN, which is a
    // different statement from `empty` ("settled with zero rows") and from
    // `error` ("the data layer failed"). Without this branch the synthetic
    // timeout digest read as "rows: 0; empty: true" and the model told the
    // user the chart has no data.
    if (d.displayed) {
      // The card is on screen and still loading. Non-ERROR on purpose: the
      // render succeeded, so this is not a tool failure and must not disarm
      // the report ride-along.
      return `NOTE: the "${d.title ?? metricName}" chart IS displayed on screen but has not finished loading, so its data cannot be summarised yet. State no figures from it. Do not claim it is empty, has no data, or failed to load — tell the user the chart is on screen and still loading.`;
    }
    //
    // Names the chart by its DISPLAY label for the same reason as every other
    // branch (#6403) — this branch was written before that landed and used the
    // raw key as its fallback.
    return `ERROR: the "${d.title ?? metricName}" chart did not finish loading in time, so its data is unknown. Do not claim it is empty or has no data — it never finished loading. Tell the user plainly the data could not be loaded right now and that they can try again.`;
  }
  if (d.error) {
    // The chart settled in an error phase — the data layer failed to load.
    // Narrate this as a FAILURE, never as "no data".
    //
    // A known cause must reach the model — see `errorReason`'s docblock.
    if (d.errorReason) {
      return `ERROR: couldn't load the "${d.title ?? metricName}" chart. REASON: ${d.errorReason}`;
    }
    return `ERROR: couldn't load the "${d.title ?? metricName}" chart — something went wrong rendering it. Do not claim there was no data; the chart failed to load.`;
  }
  // The `chart:` line is the REQUESTED description (the intent summary the
  // model itself wrote), NOT a description of what compiled. When the two
  // disagree the model used to narrate the title — e.g. a request for "each
  // location as a share of total employees" narrated as shares while the data
  // below said `metric: headcount` over 202 months (live, 2026-08-26). Label
  // it as a request and make the data authoritative.
  const lines = [
    `requested: ${d.title ?? metricName} (this is what was ASKED FOR, not proof of what rendered — the fields below are the ACTUAL chart and win any disagreement)`,
    `metric: ${metricName}; rows: ${d.rowCount}; empty: ${d.empty}`,
  ];
  if (d.postingsWeighting === 'expected_hires') {
    // The chart title still says postings, so the weighting must be named.
    lines.push(
      'WEIGHTED BY EXPECTED HIRES: every posting figure here counts each posting for the hires it is expected to yield, so it estimates hiring volume and is NOT a number of postings. Call these figures expected hires, never active, new or removed postings, and do not compare them with posting counts.'
    );
  }
  // A share was requested but the chart plots absolute values: say so plainly
  // rather than letting the request's wording become the answer. A breakdown
  // whose categories carry a native share (`shareDisplay`) DID compile the
  // share view — the topCategories line cites it — so it gets no warning.
  const plotsAbsoluteValues =
    !isPercentageMetric(d.metric) &&
    !d.topCategories?.some((c) => c.shareDisplay !== undefined);
  if (d.valueForm === 'share_of_total' && d.sharesBesideCounts) {
    lines.push(
      `BARS ARE COUNTS: each category's share of the classified total is listed below (share …), and those shares answer the question — state them. But this chart's bars plot ${metricName} counts, not shares: say plainly that the chart shows counts.`
    );
  } else if (d.valueForm === 'share_of_total' && plotsAbsoluteValues) {
    lines.push(
      `SHARE NOT AVAILABLE: this chart plots ${metricName} as absolute values, NOT shares of a total — the requested share view did not compile. Do NOT describe any figure here as a share, percentage or proportion, and do NOT say the chart shows a breakdown as a share of total. State the absolute figure for what it is, say plainly that you could not show it as a share of the workforce, and point the user to the matching Overview page for the share view.`
    );
  } else if (d.valueForm === 'unclassified' && plotsAbsoluteValues) {
    // The chart model never classified the ask (its last attempt may omit the
    // field rather than fail the chart), so whether a share was wanted is left
    // to the model narrating it, which has the user's words.
    lines.push(
      `VALUE FORM UNCLASSIFIED: this chart plots ${metricName} as absolute values, NOT shares of a total. If the user asked for a share, percentage or proportion of a total, do NOT present these figures as one: say plainly that you could not show it as a share of the workforce and point the user to the matching Overview page. Otherwise ignore this line.`
    );
  }
  if (d.filtersApplied?.length) {
    // EXPLICIT ground truth that these filters resolved + applied, so the model
    // cannot infer "unfiltered" from `rows: 1` + the bare metric and invent
    // "the filter did not resolve".
    lines.push(
      `filtersApplied: ${d.filtersApplied.join(', ')} (the latestValue already reflects these filters; do not claim one of these is unapplied)`
    );
  }
  if (d.filtersNotApplied?.length) {
    // A read refuses a filter it cannot carry; a chart renders without it.
    lines.push(
      `NOT APPLIED: ${d.filtersNotApplied.join(', ')} — this chart could not apply ${d.filtersNotApplied.length === 1 ? 'that filter' : 'those filters'}, so its figures still COUNT the people that filter would leave out. They describe the wider population, not the one asked about: never state one as the answer for the population the user asked about, and say plainly which filter could not be applied.`
    );
  }
  if (d.resolvedRoleLabel) {
    // The role filter resolved to a CONCRETE node whose label can differ from
    // the user's own term (role ids are taxonomy-relative — "nurses" can
    // resolve to a different node under a custom taxonomy). Name the resolved
    // node, never parrot the request.
    lines.push(
      `resolvedRoleLabel: ${d.resolvedRoleLabel} (the role filter resolved to THIS exact role — narrate the figure using this label, not the user's phrasing, when they differ)`
    );
  }
  if (d.resolvedSkillLabel) {
    // Skill twin of resolvedRoleLabel: skill ids are equally
    // taxonomy-relative, so ground the narration in the resolved skill(s).
    lines.push(
      `resolvedSkillLabel: ${d.resolvedSkillLabel} (the skill filter resolved to THESE exact skills — narrate the figure using these labels, not the user's phrasing, when they differ)`
    );
  }
  if (d.resolvedActivityLabel) {
    lines.push(
      `resolvedActivityLabel: ${d.resolvedActivityLabel} (the activity filter resolved to THESE exact activities — narrate the figure using these labels, not the user's phrasing, when they differ)`
    );
  }
  if (d.singleSeries) {
    lines.push(
      'SINGLE SERIES: this chart plots one line and splits it no further. Name that line by its own label, never as a total it may only be part of, and never say the chart breaks it out by role, geography or any other dimension; a breakdown the user asked for is answered only by a read that returned one.'
    );
  }
  if (d.latestValue !== undefined) {
    lines.push(`latestValue: ${d.latestValueDisplay ?? d.latestValue}`);
  }
  if (d.changePpDisplay !== undefined) {
    lines.push(`changePp: ${d.changePpDisplay}`);
  } else if (d.deltaPct !== undefined) {
    lines.push(`deltaPct: ${d.deltaPct}`);
  }
  if (d.firstDate !== undefined || d.lastDate !== undefined) {
    lines.push(`dates: ${d.firstDate ?? '?'} – ${d.lastDate ?? '?'}`);
  }
  if (d.topCategories?.length) {
    const hasShares = d.topCategories.some((c) => c.shareDisplay !== undefined);
    lines.push(
      `topCategories: ${d.topCategories.map((c) => `${c.label}=${c.displayValue ?? c.value}${c.shareDisplay !== undefined ? ` [${c.shareDisplay} of the classified total]` : ''}${c.middleHalfDisplay !== undefined ? ` [median; middle half ${c.middleHalfDisplay}]` : ''}${c.deltaPct !== undefined ? ` (${c.deltaPct > 0 ? '+' : ''}${c.deltaPct}% over the shown window)` : ''}`).join(', ')} (these are the ONLY entities/categories in this chart; narrate by these exact labels, not by what was requested; quote the values only for real-unit metrics, never for unitless internal-scale scores, which you express qualitatively per the ranked-breakdown exception in the sentiment-scores rule)${hasShares ? ' (each [N%] is that category’s share of the CLASSIFIED breakdown total — cite it verbatim for "what share/percentage is X" asks; never derive a share by dividing a count by any other total)' : ''}`
    );
  }
  // The lists above are the ends of a breakdown, not all of it: a total added
  // up from them misses whatever sits in the middle.
  const listedCategories =
    (d.topCategories?.length ?? 0) + (d.bottomCategories?.length ?? 0);
  if (d.categoryCount !== undefined && d.categoryCount > listedCategories) {
    lines.push(
      `PARTIAL LIST: ${listedCategories} of ${d.categoryCount} categories are listed. A total added up from them is a lower bound: say "at least" and name the categories it counts.`
    );
  }
  if (d.bottomCategories?.length) {
    lines.push(
      `bottomCategories (LOWEST values, worst first): ${d.bottomCategories.map((c) => `${c.label}=${c.displayValue ?? c.value}${c.middleHalfDisplay !== undefined ? ` [median; middle half ${c.middleHalfDisplay}]` : ''}`).join(', ')} (the ranked worst end of the same breakdown — cite these exact labels when the question asks about problems, dissatisfaction, or why people leave; quote the values only for real-unit metrics, never for unitless internal-scale scores, which you express qualitatively per the ranked-breakdown exception in the sentiment-scores rule)`
    );
  }
  return lines.join('\n');
}

/** Model-facing serialization of a filter-resolution digest (apply_filters). */
export function filterSummary(d: FilterResolutionDigest): string {
  const applied = d.applied
    .map((f) => `${f.requested} -> ${f.resolvedTo} (${f.filterId})`)
    .join('; ');
  const unresolved = d.unresolved
    .map((f) => `${f.requested}${f.reason ? ` (${f.reason})` : ''}`)
    .join('; ');
  return `FILTERS on ${d.targetPage}\nAPPLIED: ${applied || 'none'}\nUNRESOLVED: ${unresolved || 'none'}`;
}
